import { Navigate } from "react-router"
import styled from "styled-components"

import CommunityPosts from "~/components/CommunityPosts"
import Layout, { PageTitle } from "~/components/Layout"
import { RouteTabs } from "~/components/Tabs"
import { useUser } from "~/contexts/UserContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import Channels from "./Channels"
import CommunityProfiles from "./CommunityProfiles"
import CommunitySettings from "./CommunitySettings"
import CreateCommunityProfile from "./CreateCommunityProfile"
import Leaderboard from "./Leaderboard"
import Messages from "./Messages"
import ModerationQueue from "./ModerationQueue"
import PostCategories from "./PostCategories"

const header = <PageTitle>Community</PageTitle>

function toNotificationSticker(count: number | undefined): string | undefined {
	if (count === undefined || count === 0) return undefined
	return count > 99 ? "99+" : count.toString()
}

const CommunityPage = withAccessRequirement("community", header, () => {
	const user = useUser()
	const platform = user.user!.platform!
	const notificationCounts = query.community.getCreatorNotificationCounts.useQuery(undefined, {
		enabled: user.user!.communityProfileId !== null,
	})

	if (platform.community === null) return <Navigate to={routes.community.index()} />

	return (
		<Layout>
			{header}
			{user.user!.communityProfileId === null ? (
				<CreateCommunityProfile />
			) : (
				<RouteTabs
					tabs={[
						{
							title: "Posts",
							sticker: toNotificationSticker(notificationCounts.data?.posts),
							route: routes.community.posts(),
							content: (
								<TabContent>
									<CommunityPosts />
								</TabContent>
							),
						},
						{
							title: "Moderation",
							sticker: toNotificationSticker(notificationCounts.data?.moderation),
							route: routes.community.moderation(),
							content: (
								<TabContent>
									<ModerationQueue />
								</TabContent>
							),
						},
						{
							title: "Channels",
							route: routes.community.channels(),
							content: (
								<TabContent>
									<Channels />
								</TabContent>
							),
						},
						{
							title: "Post categories",
							route: routes.community.postCategories(),
							content: (
								<TabContent>
									<PostCategories />
								</TabContent>
							),
						},
						{
							title: "Profiles",
							route: routes.community.profiles(),
							content: (
								<TabContent>
									<CommunityProfiles />
								</TabContent>
							),
						},
						...(platform.community.isGamificationEnabled
							? [
									{
										title: "Leaderboard",
										route: routes.community.leaderboard(),
										content: (
											<TabContent>
												<Leaderboard />
											</TabContent>
										),
									},
								]
							: []),
						{
							title: "Messages",
							sticker: toNotificationSticker(notificationCounts.data?.messages),
							route: routes.community.messages(),
							content: (
								<TabContent>
									<Messages />
								</TabContent>
							),
						},
						{
							title: "Settings",
							route: routes.community.settings(),
							content: (
								<TabContent>
									<CommunitySettings />
								</TabContent>
							),
						},
					]}
				/>
			)}
		</Layout>
	)
})

const TabContent = styled.div`
	margin-top: 20px;
`

export default CommunityPage
